/**
 * Typeahead over the card catalogue.
 *
 * Three things this has to get right, and each is a bug in most hand-rolled
 * autocompletes:
 *
 *  1. **Stale responses.** Typing "cha" then "char" fires two requests, and the
 *     slower one can land second. Every request carries an AbortController and the
 *     previous one is cancelled, so the list can never show results for a query
 *     that is no longer in the box.
 *
 *  2. **The keyboard.** Arrow keys move, Enter picks, Escape closes, Tab leaves.
 *     A dropdown you can only use with a mouse is worse than no dropdown, because
 *     it takes the Enter key away from the form it sits in.
 *
 *  3. **Not getting in the way.** What someone typed is always a valid answer
 *     to THIS control — a want or an offered card may be anything. Whether it
 *     is a valid answer to the form is the form's rule, not this one's: since
 *     2026-10-12 the posting screen does not post a title the catalogue does
 *     not know (`post.jsx` says why), and this list is how a printing is picked.
 */
import React from "react";
import { gameLabel } from "./data.jsx";
import { api, suggestionNameFor } from "./api.jsx";
import { track } from "./analytics.jsx";
import { useApp } from "./store.jsx";

const AC = window.CardboardmarketDesignSystem_2a3239;

/** Waits for a pause in typing. 180ms is about a fast typist's gap between keys. */
function useDebounced(value, ms) {
  const [settled, setSettled] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * `value` / `onChange` are the text, exactly like an Input.
 * `onPick` additionally receives the whole suggestion when one is chosen, so a
 * caller can fill in the game and set as well as the name.
 */
export function CardAutocomplete({
  value, onChange, onPick, game, kind,
  label, placeholder, hint, icon,
  // Called when Enter was NOT used to pick a suggestion — i.e. when the typist
  // meant "I am done" rather than "that one". A caller cannot do this through
  // fieldProps: this component sets its own onKeyDown, which would replace it.
  onEnter,
  Field = AC.Input, fieldProps = {},
  // The style of the box around the field and its list — which is what a flex
  // row lays out, so a caller sizes the control here rather than on the field.
  boxStyle,
}) {
  // Defaults live in the body, not the parameter list: a default parameter is
  // evaluated in the CALLER's scope, where the translator does not exist.
  const { t } = useApp();
  const fieldLabel = label === undefined ? t("Card") : label;
  const fieldPlaceholder = placeholder === undefined ? t("Start typing a card name") : placeholder;
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [active, setActive] = React.useState(-1);
  const [loading, setLoading] = React.useState(false);
  const query = useDebounced(value, 180);
  const boxRef = React.useRef(null);
  const listId = React.useRef(`ac-${Math.random().toString(36).slice(2)}`).current;
  // Suppresses the fetch that a selection would otherwise trigger: picking sets the
  // text, and reopening the list over the answer someone just chose is maddening.
  const justPicked = React.useRef(false);
  /*
   * THE LAST TEXT THIS BOX ITSELF PRODUCED, which is how it tells typing from
   * being filled in.
   *
   * Reported against the scanner: "the auto fill doesn't. It doesn't fill the set
   * name and all". It did fill it — and then opened its suggestion list on top of
   * the filled fields, over the very answer the seller had just chosen from a
   * list of pictures. A form that covers itself the moment it is answered reads
   * as a form that did nothing.
   *
   * A value that does not match what was last typed here came from somewhere
   * else, and somewhere else has already decided. Typing sets this, so the list
   * comes back the instant somebody edits the name.
   */
  const typed = React.useRef(null);

  React.useEffect(() => {
    if (justPicked.current) { justPicked.current = false; return; }
    // Filled in from outside — a scan, an edit, a restored draft. Nothing to
    // suggest: the answer is already in the box.
    if (typed.current !== value) { setItems([]); setLoading(false); setOpen(false); return; }
    const q = (query || '').trim();
    /*
     * ONE character is a real search, and it was refused here as well as at the
     * API. "N" is a Pokémon Trainer card with 60 printings and "7" is a real
     * Yu-Gi-Oh card; XY, 151 and D23 are real sets. The two-character floor made
     * all of them untypeable, and the guard that matters — the 180ms debounce
     * above and the server's limit — is unchanged.
     *
     * The `@trader` search deliberately gets no suggestions: this list is the
     * card CATALOGUE, and a catalogue has never heard of a trader. Offering card
     * names under a query that is not looking for cards would be answering a
     * different question than the one being asked.
     */
    if (q.length < 1 || q.startsWith('@')) { setItems([]); setLoading(false); return; }

    const controller = new AbortController();
    setLoading(true);
    /*
     * TWENTY, which is the most the server gives, and not eight.
     *
     * On 2026-10-12 a seller typed "Rowlet" holding the one from Perfect Order,
     * saw eight Rowlets ordered by the length of their attack names, none of
     * them theirs, picked the nearest and got the wrong picture. The catalogue
     * has sixteen Rowlets. A list that shows half of what it knows is asking
     * the seller to guess which half the card is in; the list below scrolls
     * inside itself, so the extra rows cost the page nothing.
     */
    api.searchSuggestions({ q, game, kind, limit: 20 }, controller.signal)
      .then((r) => { setItems(r.suggestions); setActive(-1); setOpen(true); })
      // An abort is the expected outcome of typing another character, not a failure
      // worth showing anyone.
      .catch((e) => { if (e.name !== 'AbortError') setItems([]); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });

    return () => controller.abort();
  }, [query, game, kind]);

  // Clicking away closes it. Pointerdown rather than click, so a pick still lands.
  React.useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, []);

  const choose = (item) => {
    // Whether suggestions are actually used decides if the catalogue ingest is
    // earning its keep.
    track('suggest.pick');
    justPicked.current = true;
    /*
     * THE NAME IT WAS FOUND BY stays in the box — "Elfos de Llanowar", not
     * "Llanowar Elves" (the owner, 2026-10-22). `item.name` is still the
     * English one, and a caller that stores or searches takes it from the
     * pick, never from the text.
     */
    onChange(suggestionNameFor(item));
    if (onPick) onPick(item);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || !items.length) {
      if (e.key === 'ArrowDown' && items.length) setOpen(true);
      else if (e.key === 'Enter' && onEnter) onEnter(e);
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % items.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i <= 0 ? items.length : i) - 1); }
    else if (e.key === 'Enter') {
      // Only swallow Enter when something is actually highlighted. Otherwise the
      // form this sits in should submit, which is what the typist expects.
      if (active >= 0) { e.preventDefault(); choose(items[active]); }
      else { setOpen(false); if (onEnter) onEnter(e); }
    } else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    else if (e.key === 'Tab') setOpen(false);
  };

  const showList = open && items.length > 0;

  /*
   * THE LIST IS TALLER THAN ITS BOX NOW, so the box has to follow the keyboard.
   *
   * Twenty rows at ~36px is 720px inside a box of 280, and before this the
   * eight rows all but fit — so arrowing down past the fold used to be a
   * corner and is now the normal case. The highlighted row is scrolled into
   * view, "nearest" so the list does not jump when the row is already on
   * screen. And a fresh answer starts at the top, because the scroll position
   * of the previous answer means nothing for this one.
   */
  const listRef = React.useRef(null);
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const row = active >= 0 ? list.children[active] : null;
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [active]);
  React.useEffect(() => { if (listRef.current) listRef.current.scrollTop = 0; }, [items]);

  return (
    <div ref={boxRef} style={{ ...boxStyle, position: 'relative' }}>
      <Field
        {...fieldProps}
        label={fieldLabel}
        icon={icon}
        hint={hint}
        placeholder={fieldPlaceholder}
        value={value}
        onChange={(e) => { const v = e && e.target ? e.target.value : e; typed.current = v; onChange(v); }}
        onKeyDown={onKeyDown}
        onFocus={() => items.length && setOpen(true)}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
      />
      {showList ? (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          /*
           * BOUNDED, AND SCROLLING INSIDE ITSELF. Floated over the form rather
           * than pushed into it, so opening it moves nothing below; and never
           * taller than 280px however many rows it holds, so twenty rows cover
           * exactly what eight did — on a phone, the next field or two, which
           * a pick or Escape gives back. A list that grew with its rows would
           * cover the whole form on the width that matters.
           */
          style={{
            position: 'absolute', zIndex: 40, left: 0, right: 0, top: '100%', marginTop: 4,
            listStyle: 'none', padding: 4, margin: 0, maxHeight: 280, overflowY: 'auto',
            background: 'var(--white)', border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-2)',
          }}
        >
          {items.map((item, i) => (
            <li
              key={`${item.name}-${item.cardCode || i}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // Pointerdown, not click: a click fires after blur, by which time the
              // list has closed and the selection is lost.
              onPointerDown={(e) => { e.preventDefault(); choose(item); }}
              onMouseEnter={() => setActive(i)}
              style={{
                display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 10px', cursor: 'pointer',
                borderRadius: 'var(--radius-sm)',
                background: i === active ? 'var(--surface-sunken)' : 'transparent',
              }}
            >
              <span style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {suggestionNameFor(item)}
              </span>
              <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', whiteSpace: 'nowrap', ...(suggestionNameFor(item) !== item.name ? { overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '50%' } : {}) }}>
                {/* The English name beside a name in another language, small, so a
                    seller who knows the card by either can still tell it apart. */}
                {suggestionNameFor(item) !== item.name ? `${item.name} · ` : ''}
                {gameLabel(item.game)}
                {item.printings > 1 ? ` · ${t("{count} printings", { count: item.printings })}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {loading && value.trim().length >= 1 && !showList ? (
        <span style={{ position: 'absolute', right: 10, top: 34, font: 'var(--type-meta)', color: 'var(--text-faint)' }}>…</span>
      ) : null}
    </div>
  );
}

